/**
 * pdp-video-cover — sterowanie wideo produktowym w galerii PDP.
 *
 * Element <video> przychodzi z Liquid BEZ atrybutu src, wiec do momentu decyzji nie leci
 * zadne zadanie sieciowe. Zrodlo wybieramy dopiero przy wejsciu slajdu w widoczny obszar.
 *
 * Pauzowanie opiera sie na IntersectionObserver, a nie na API slidera Embla: slajdy leza
 * obok siebie w poziomie, wiec zjazd na kolejny slajd naturalnie wyprowadza element poza
 * widoczny obszar.
 *
 * Wideo odslaniamy na zdarzeniu "playing", czyli gdy jest juz pierwsza klatka — nie
 * w momencie rozpoczecia pobierania. Inaczej poster znikalby przed czasem i powstawalaby
 * biala dziura (ten sam blad ma Ecomus w base.css:3591, gdzie poster gasnie na [loaded]).
 */
(() => {
  'use strict';

  const MOBILE_QUERY = '(max-width: 767px)';

  class PdpVideoCover extends HTMLElement {
    connectedCallback() {
      // Guard idempotencji nizej zaklada, ze dzieci elementu sa niezmienne: nasluch "playing"
      // przypinamy raz, do wezla <video> znalezionego przy pierwszym podlaczeniu. Gdyby ktos
      // podmienil zawartosc <pdp-video-cover> miedzy disconnect a connect, listener zostalby
      // przy starym wezle — atrybut "playing" nigdy by sie nie ustawil, a sourceSet wskazywalby
      // na zrodlo, ktorego juz nie ma (wideo graloby na zawsze pod posterem). Dzis takiej sciezki
      // nie ma: snippet renderuje sie raz, a re-render sekcji tworzy nowy element z initialized
      // rownym undefined. Swiadomie nie budujemy tu obrony — kosztowalaby wiecej, niz daje.
      this.video = this.querySelector('video');
      if (!this.video) return;

      // connectedCallback potrafi zadzialac wiecej niz raz na tym samym egzemplarzu (przeniesienie
      // wezla w DOM = disconnect + connect). Jednorazowa inicjalizacja trzyma sie za tym guardem,
      // zeby przeniesienie nie dopinalo drugiego listenera i nie kasowalo stanu sourceSet
      // (skasowany oznaczalby ponowne przypisanie src, czyli przeladowanie wideo od zera).
      if (!this.initialized) {
        this.initialized = true;
        this.sourceSet = false;
        this.userActivated = false;

        // "playing" raz ustawione nie jest juz zdejmowane — to celowe. Atrybut nie znaczy
        // "gra teraz", tylko "jest juz pierwsza klatka", i trzyma warstwe wideo na opacity: 1.
        // Dzieki temu pauza zostawia zamrozony kadr zamiast wracac skokiem do postera.
        this.video.addEventListener('playing', () => {
          this.setAttribute('playing', '');
          this.removeAttribute('suspended');
        });

        // Podpiecie przycisku nalezy do jednorazowej inicjalizacji z tego samego powodu, co
        // nasluch "playing": przycisk jest dzieckiem tego elementu, wiec przeniesienie wezla
        // zabiera go ze soba razem z listenerem. Ponowne addEventListener dopielo by druga
        // kopie (arrow function to za kazdym razem nowa referencja, wiec przegladarka jej nie
        // deduplikuje), a jedno klikniecie wywolaloby toggleByUser dwa razy — pauza i natychmiast
        // wznowienie, czyli przycisk przestalby cokolwiek robic.
        const toggle = this.querySelector('[data-video-toggle]');
        if (toggle) {
          toggle.addEventListener('click', () => this.toggleByUser());
        }
      }

      // Podpiecie przycisku musi byc wyzej niz to wyjscie: inaczej uzytkownik z ograniczona
      // animacja dostaje widoczny przycisk, ktory nic nie robi.
      // Wyjscie PRZED this.observe() oznacza, ze IntersectionObserver w ogole nie powstaje,
      // a src nigdy nie zostaje ustawiony — zero pobierania. Nasluch "playing" jest podpiety
      // wyzej i zostaje, bo wideo wolno uruchomic recznie takze w tym stanie.
      // Warunek this.userActivated: gdy uzytkownik sam uruchomil wideo przyciskiem, ponowne
      // podlaczenie wezla nie moze cofnac tej decyzji z powrotem do trybu statycznego.
      if (!this.userActivated && this.shouldStayStatic()) {
        this.setAttribute('static', '');
        return;
      }

      this.observe();
    }

    observe() {
      if (this.observer) return;
      // entries[entries.length - 1] zamiast entries[0]: IntersectionObserver potrafi dostarczyc
      // kilka wpisow dla tego samego celu w jednej paczce, liczy sie stan najswiezszy.
      // threshold: 0 — startujemy przy dowolnej widocznosci, pauzujemy dopiero przy pelnym
      // zniknieciu. Wyzszy prog niczego by tu nie zmienil, bo sterujemy sie flaga isIntersecting,
      // ktora jest prawdziwa juz przy jednym widocznym pikselu. Dokladalby za to zbedne
      // wywolania: specyfikacja powiadamia takze przy zejsciu ponizej progu, wiec przy 0.25
      // dostawalismy dodatkowe play() dokladnie w chwili wyjezdzania elementu z ekranu —
      // czyli tuz przed pause(), co produkowalo wiszace obietnice konczace sie AbortError.
      this.observer = new IntersectionObserver(
        (entries) => this.onIntersect(entries[entries.length - 1].isIntersecting),
        { threshold: 0 }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        // Wyzerowanie referencji jest obowiazkowe: observe() wychodzi wczesniej, gdy this.observer
        // jest prawdziwe, wiec po ponownym podlaczeniu element zostalby bez obserwatora na stale.
        this.observer = null;
      }

      // Sam obserwator nie wystarczy: odlaczony element z ustawionym src gra dalej i dociaga
      // plik (do 2,8 MiB na desktopie), dopoki nie zbierze go GC. Guard sourceSet, bo bez zrodla
      // nie ma czego zatrzymywac — a przy braku <video> (wczesny return w connectedCallback)
      // sourceSet jest undefined, wiec ten sam guard chroni przed odczytem z niezdefiniowanego pola.
      if (this.sourceSet) {
        this.video.pause();
      }
    }

    shouldStayStatic() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      return Boolean(connection && connection.saveData);
    }

    onIntersect(isVisible) {
      // Bez tej linii powrot na slajd wznawialby odtwarzanie mimo swiadomego zatrzymania.
      if (this.hasAttribute('user-paused')) return;

      if (isVisible) {
        this.play();
      } else if (this.sourceSet) {
        this.video.pause();
      }
    }

    toggleByUser() {
      // Warunek pokrywa dokladnie te stany, dla ktorych CSS pokazuje etykiete "Odtworz wideo"
      // ([user-paused], [static], [suspended]). Rozjazd oznaczalby przycisk robiacy co innego,
      // niz obiecuje jego etykieta.
      if (this.hasAttribute('user-paused') || this.hasAttribute('static') || this.hasAttribute('suspended')) {
        this.userActivated = true;
        this.removeAttribute('user-paused');
        this.removeAttribute('static');
        // "suspended" zdejmuje dopiero nasluch "playing", czyli faktyczna pierwsza klatka.
        // Skasowanie go tutaj przelaczyloby etykiete na "Zatrzymaj wideo", zanim cokolwiek
        // ruszy — a przy ponownie odrzuconym play() etykieta klamalaby az do konca sesji.
        // W trybie statycznym obserwator nie powstal — tworzymy go dopiero teraz, zeby
        // po recznym uruchomieniu dzialala pauza przy zjezdzie ze slajdu.
        this.observe();
        this.play();
      } else {
        this.setAttribute('user-paused', '');
        this.video.pause();
      }
    }

    play() {
      if (!this.sourceSet) {
        const isMobile = window.matchMedia(MOBILE_QUERY).matches;
        const src = isMobile ? this.dataset.srcMobile : this.dataset.srcDesktop;
        if (!src) return;
        this.video.src = src;
        this.sourceSet = true;
      }

      const attempt = this.video.play();
      if (attempt && typeof attempt.catch === 'function') {
        // Odrzucona obietnica play() nie zawsze oznacza odrzucony autoplay. video.pause() konczy
        // wiszace play() bledem AbortError, a pause() wolamy sami — w onIntersect i przy
        // odlaczeniu elementu. Bez tego rozroznienia wejscie na PDP i natychmiastowe przewiniecie
        // strony (zanim wideo sie rozbuforuje) ustawialoby "suspended", czyli stan znaczacy
        // "przegladarka odmowila". Etykieta przycisku mrugalaby wtedy na "Odtworz wideo",
        // a atrybut przestalby byc wiarygodnym sygnalem diagnostycznym.
        // Nierozpoznane odrzucenie traktujemy jak odmowe — konserwatywnie, bo wtedy przycisk
        // zostaje widoczny i uzytkownik ma czym uruchomic wideo.
        attempt.catch((err) => {
          if (err && err.name === 'AbortError') return;
          this.setAttribute('suspended', '');
        });
      }
    }
  }

  if (!customElements.get('pdp-video-cover')) {
    customElements.define('pdp-video-cover', PdpVideoCover);
  }
})();
